import { useRouter } from 'expo-router'
import type { PropsWithChildren, ReactNode } from 'react'
import { View } from 'react-native'

import { Card, CardBody, CardHeader } from '@/src/elements/card'
import { Image } from '@/src/elements/image'
import { Listbox, ListboxItem, type TListboxItemProps } from '@/src/elements/listbox'
import { Icon } from '@/src/elements/media/Icon'
import { Typography } from '@/src/elements/text/Typography'
import { GuideLinks } from '@/src/features/guide/ui/components/GuideLinks'
import { TEST_ID } from '@/src/general/constants/testId'
import type { ICON } from '@/src/general/enums/icons'

import AppJson from '@/app.json'

type TMenuListboxProps = {
  bold?: boolean
  items: TListboxItemProps[]
}

const MenuListbox = ({ bold, items }: TMenuListboxProps): ReactNode => (
  <Listbox items={items}>{(props, index) => <ListboxItem bold={bold} key={index} {...props} />}</Listbox>
)

type TMenuItemAlertProps = {
  icon?: ICON
  children: ReactNode
  navigateTo?: string
}

const AlertItem = ({ children, icon, navigateTo }: TMenuItemAlertProps): ReactNode => {
  const { push } = useRouter()

  const handleNavigation = () => {
    if (navigateTo) push(navigateTo)
  }

  return (
    <Card
      isPressable
      isHoverable
      shadow="xs"
      onPress={handleNavigation}
      className="my-6 items-center gap-2 bg-primary-bg-medium p-4"
    >
      {icon != null && (
        <CardHeader testID={TEST_ID.MISCELLANEOUS.ICON_ALERT_ITEM} className="native:w-4 p-0 items-center">
          <Icon size="lg" className="text-center text-primary-text-contrast-default" name={icon} />
        </CardHeader>
      )}
      <CardBody className="p-0">
        <Typography bold colors="primary" className="text-center">
          {children}
        </Typography>
      </CardBody>
    </Card>
  )
}

const TitleItem = ({ children }: PropsWithChildren): ReactNode => (
  <View className="px-6 pb-2 pt-4">
    <Typography contrast="low" size="sm" className=" text-center">
      {children}
    </Typography>
  </View>
)

const GuidesItem = (): ReactNode => (
  <View className="pb-3.5">
    <GuideLinks />
  </View>
)

const LogoItem = (): ReactNode => (
  <View testID={TEST_ID.MENU.ITEM.LOGO} className="mx-auto h-20 min-h-20 w-full py-6">
    <Image source={require('@/img/menu/hozana.png')} className="h-20 w-40" />
  </View>
)

const VersionItem = (): ReactNode => (
  <View className="mb-4 items-center justify-center pb-4">
    <Typography colors="secondary" size="sm" className="opacity-50">
      {AppJson.expo.version}
    </Typography>
  </View>
)

export const MenuItem = {
  Listbox: MenuListbox,
  Alert: AlertItem,
  Title: TitleItem,
  Guides: GuidesItem,
  Logo: LogoItem,
  Version: VersionItem,
}
